export async function fileToWebpFile(
    file: File,
    quality = 0.7,
    maxW = 1200,
    maxH = 1200,
): Promise<File> {
    if (!file.type.startsWith("image/")) return file;

    const safari = isSafariBrowser();
    const targetMimeType = safari ? "image/jpeg" : "image/webp";
    const targetExt = safari ? "jpg" : "webp";

    if (!canEncodeMimeType(targetMimeType)) return file;

    const objectUrl = URL.createObjectURL(file);
    try {
        const source = await loadImageSource(file, objectUrl);
        const width = getSourceWidth(source);
        const height = getSourceHeight(source);
        if (!width || !height) return file;

        const k = Math.min(1, maxW / width, maxH / height);
        const targetW = Math.round(width * k);
        const targetH = Math.round(height * k);

        const canvas = document.createElement("canvas");
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext("2d");
        if (!ctx) return file;

        ctx.drawImage(source, 0, 0, targetW, targetH);
        if (isImageBitmap(source)) source.close();

        const convertedBlob = await toBlob(canvas, targetMimeType, quality);

        if (!convertedBlob) return file;

        if (convertedBlob.size > file.size) return file;

        const outputName = toNameWithExtension(file.name, targetExt);
        return new File([convertedBlob], outputName, {
            type: targetMimeType,
            lastModified: Date.now(),
        });
    } catch {
        return file;
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
}

async function loadImageSource(
    file: File,
    objectUrl: string,
): Promise<ImageBitmap | HTMLImageElement> {
    if (typeof createImageBitmap === "function") {
        try {
            return await createImageBitmap(file);
        } catch {
            // Safari fallback.
        }
    }

    return await loadHtmlImage(objectUrl);
}

function loadHtmlImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = src;
    });
}

function getSourceWidth(source: ImageBitmap | HTMLImageElement): number {
    return isImageBitmap(source) ? source.width : source.naturalWidth;
}

function getSourceHeight(source: ImageBitmap | HTMLImageElement): number {
    return isImageBitmap(source) ? source.height : source.naturalHeight;
}

function isImageBitmap(
    source: ImageBitmap | HTMLImageElement,
): source is ImageBitmap {
    return "close" in source;
}

function toBlob(
    canvas: HTMLCanvasElement,
    mimeType: string,
    quality?: number,
): Promise<Blob | null> {
    return new Promise((resolve) => {
        canvas.toBlob(resolve, mimeType, quality);
    });
}

const mimeSupportCache = new Map<string, boolean>();

function canEncodeMimeType(mimeType: string): boolean {
    const cached = mimeSupportCache.get(mimeType);
    if (cached !== undefined) return cached;

    const canvas = document.createElement("canvas");
    const supported = canvas.toDataURL(mimeType).startsWith(`data:${mimeType}`);
    mimeSupportCache.set(mimeType, supported);
    return supported;
}

function isSafariBrowser(): boolean {
    if (typeof navigator === "undefined") return false;

    const ua = navigator.userAgent;
    const isSafari =
        /Safari/i.test(ua) &&
        !/Chrome|CriOS|FxiOS|Edg|EdgiOS|OPR|Android/i.test(ua);
    return isSafari;
}

function toNameWithExtension(name: string, ext: string): string {
    const i = name.lastIndexOf(".");
    if (i === -1) return `${name}.${ext}`;
    return `${name.slice(0, i)}.${ext}`;
}
